import React, { useState, useEffect, useCallback } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import useEmblaCarousel from 'embla-carousel-react';

interface GooglePlaceReview {
  authorName: string;
  rating: number;
  text: string;
  time: number;
  relativeTime: string;
}

interface ReviewWithSite extends GooglePlaceReview {
  siteName: string;
}

interface RecentReviewsProps {
  title?: string;
  className?: string;
  maxReviews?: number;
  showCarousel?: boolean;
}

const RecentReviews: React.FC<RecentReviewsProps> = ({ 
  title = "Avis clients récents", 
  className = '',
  maxReviews = 9,
  showCarousel = true
}) => {
  const [reviews, setReviews] = useState<ReviewWithSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'start',
    skipSnaps: false,
    dragFree: true
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi]);

  useEffect(() => {
    const fetchRecentReviews = async () => {
      try {
        // Fetch more sites for better variety in the carousel
        const topSites = [
          { name: "Profit recyclage", address: "8/10 Rue Denis Papin", city: "Beauchamp" },
          { name: "Bellé recyclage", address: "Camp Franc", city: "Sainte-Radegonde" },
          { name: "Casse Auto du Bassin", address: "La Grange LIEU DIT", city: "Viviez" },
          { name: "Fabrude Recyclage", address: "Prentegarde, D120", city: "Saint-Paul-des-Landes" },
          { name: "Lescout Recyclage", address: "484 Chem. des Vignes", city: "Lescout" },
          { name: "Carmausine de récupération", address: "Zone Industriel Centrale", city: "Carmaux" }
        ];

        const allReviews: ReviewWithSite[] = [];

        for (const site of topSites) {
          try {
            const { data: result } = await supabase.functions.invoke('google-places-details', {
              body: { siteName: site.name, address: site.address, city: site.city }
            });

            if (result && result.reviews) {
              const siteReviews = result.reviews.map((review: GooglePlaceReview) => ({
                ...review,
                siteName: site.name
              }));
              allReviews.push(...siteReviews);
            }
          } catch (error) {
            console.error(`Error fetching reviews for ${site.name}:`, error);
          }
        }

        // Sort by time and take the most recent ones
        allReviews.sort((a, b) => b.time - a.time);
        setReviews(allReviews.slice(0, maxReviews));
      } catch (error) {
        console.error('Error fetching recent reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentReviews();
  }, [maxReviews]);

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star 
          key={i} 
          className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
        />
      );
    }
    return stars;
  };

  if (loading || reviews.length === 0) {
    return null;
  }

  // If not using carousel or too few reviews, show regular grid
  if (!showCarousel || reviews.length <= 3) {
    return (
      <section className={`py-16 bg-gradient-to-br from-white to-slate-50 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-fabre-gray mb-4">
              {title}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Découvrez les derniers retours de nos clients sur nos centres de recyclage
            </p>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {reviews.slice(0, 3).map((review, index) => (
              <Card key={`${review.siteName}-${index}`} className="border border-border hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1">{review.authorName}</h3>
                      <p className="text-sm text-primary/80 font-medium mb-2">{review.siteName}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {renderStars(review.rating)}
                        </div>
                        <span className="text-sm text-muted-foreground">{review.relativeTime}</span>
                      </div>
                    </div>
                    <Quote className="w-5 h-5 text-primary/60 flex-shrink-0 ml-2" />
                  </div>
                  
                  <p className="text-muted-foreground leading-relaxed text-sm line-clamp-4">
                    {review.text}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }


  // Carousel layout
  return (
    <section className={`py-16 bg-gradient-to-br from-white to-slate-50 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-fabre-gray mb-4">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Découvrez les retours de nos clients sur nos centres de recyclage
          </p>
        </div>
        
        <div className="relative max-w-7xl mx-auto">
          {/* Navigation buttons */}
          <div className="flex justify-center gap-4 mb-8">
            <Button
              variant="outline"
              size="icon"
              onClick={scrollPrev}
              className="rounded-full bg-white/80 backdrop-blur-sm border-primary/20 hover:bg-primary/10 transition-all duration-300"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={scrollNext}
              className="rounded-full bg-white/80 backdrop-blur-sm border-primary/20 hover:bg-primary/10 transition-all duration-300"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {reviews.map((review, index) => (
                <div key={`${review.siteName}-${index}`} className="flex-[0_0_320px] min-w-0">
                  <Card className="border border-border hover:shadow-lg transition-all duration-300 h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground mb-1">{review.authorName}</h3>
                          <p className="text-sm text-primary/80 font-medium mb-2">{review.siteName}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {renderStars(review.rating)}
                            </div>
                            <span className="text-sm text-muted-foreground">{review.relativeTime}</span>
                          </div>
                        </div>
                        <Quote className="w-5 h-5 text-primary/60 flex-shrink-0 ml-2" />
                      </div>
                      
                      <p className="text-muted-foreground leading-relaxed text-sm line-clamp-4">
                        {review.text}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="flex justify-center mt-8">
            <div className="text-sm text-muted-foreground bg-white/60 backdrop-blur-sm rounded-full px-4 py-2 border border-primary/10">
              {reviews.length} avis récents
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecentReviews;